import { useState } from 'react';

import { FavoritesScreen } from '../pages/favorites/FavoritesScreen';
import { PaymentScreen } from '../pages/payment/PaymentScreen';
import { ProfileScreen } from '../pages/profile/ProfileScreen';
import { FiltersScreen } from '../pages/search/FiltersScreen';
import { SubscriptionsScreen } from '../pages/subscriptions/SubscriptionsScreen';
import { UpgradeProvider } from '../shared/upgrade';

/**
 * Оболочка приложения (T11.1, T11.3).
 *
 * ## Четыре вкладки, а не пять и не три
 *
 * В макете их пять: Радар, Поиск, Избранное, Уведомления, Профиль. В
 * `09_UI_UX.md` §3 записаны три, и пять там отвергнуты как избыточные.
 * Здесь четыре, и вот почему.
 *
 * «Радар» оставлен и сделан экраном открытия. Это не «Главная», от
 * которой отказались: та дублировала поиск с пустым фильтром, а радар
 * показывает счёт находок за окно и лучшее из найденного — то самое, за
 * что платят. Экран, который продаёт, обязан открываться первым.
 *
 * «Уведомления» убраны в профиль. История приходивших карточек
 * открывается раз в месяц, а место в главной навигации стоит дорого:
 * пятая вкладка отнимает пятую часть у тех, что работают ежедневно.
 *
 * «Избранное» осталось вкладкой вопреки §3. Сравнение двух-трёх машин —
 * ровно то, ради чего человек вернётся завтра, и прятать это в профиль
 * значит прятать повод вернуться.
 */

type Tab = 'radar' | 'search' | 'favorites' | 'profile';

interface TabInfo {
  readonly id: Tab;
  readonly glyph: string;
  readonly label: string;
  readonly title: string;
  readonly subtitle: string;
}

const TABS: readonly TabInfo[] = [
  {
    id: 'radar',
    glyph: '◉',
    label: 'Радар',
    title: 'Мой радар',
    subtitle: 'Следим за рынком круглосуточно',
  },
  { id: 'search', glyph: '⌕', label: 'Поиск', title: 'Поиск', subtitle: 'Фильтры и результаты' },
  {
    id: 'favorites',
    glyph: '★',
    label: 'Избранное',
    title: 'Избранное',
    subtitle: 'То, что вы отложили сравнить',
  },
  {
    id: 'profile',
    glyph: '○',
    label: 'Профиль',
    title: 'Профиль',
    subtitle: 'Тариф, уведомления, настройки',
  },
];

export function App(): React.JSX.Element {
  const [active, setActive] = useState<Tab>('radar');
  // Оплата открывается поверх вкладок, а не вкладкой: она не место, где
  // живут, а ответ на упор в лимит (`09_UI_UX.md` §8), и вернуться из
  // неё человек должен туда, откуда пришёл
  const [paying, setPaying] = useState(false);
  const screen = TABS.find((tab) => tab.id === active) ?? TABS[0]!;

  return (
    <UpgradeProvider
      onUpgrade={() => {
        setPaying(true);
      }}
    >
      <main className="screen">
        {paying ? (
          <PaymentScreen
            onBack={() => {
              setPaying(false);
            }}
          />
        ) : null}

        {/*
          Вкладки не размонтируются, а прячутся: убери их из дерева — и
          вместе с ними пропали бы загруженные страницы выдачи и место
          прокрутки, куда человек вернётся после оплаты
        */}
        <div className={paying ? 'flow flow--hidden' : 'flow'}>
          <h1 className="screen__title">{screen.title}</h1>
          <p className="screen__subtitle">{screen.subtitle}</p>

          {active === 'radar' ? (
            <SubscriptionsScreen
              onCreate={() => {
                setActive('search');
              }}
            />
          ) : null}
          {active === 'search' ? <FiltersScreen /> : null}
          {active === 'favorites' ? <FavoritesScreen /> : null}
          {active === 'profile' ? (
            <ProfileScreen
              onFavorites={() => {
                setActive('favorites');
              }}
            />
          ) : null}
        </div>
      </main>

      <nav className="tabbar">
        {TABS.map((tab) => (
          <button
            key={tab.id}
            type="button"
            className="tabbar__item"
            aria-current={tab.id === active ? 'page' : undefined}
            onClick={() => {
              setActive(tab.id);
            }}
          >
            <span className="tabbar__glyph" aria-hidden="true">
              {tab.glyph}
            </span>
            {tab.label}
          </button>
        ))}
      </nav>
    </UpgradeProvider>
  );
}
